// src/lib/DpAnalytics.ts
import { Analytics } from "analytics";
import googleAnalytics from "@analytics/google-analytics";
import MD5 from "crypto-js/md5.js";

// src/lib/types/lib.types.ts
var FrontEndActions = [
  "click",
  "edit",
  "filter",
  "hide",
  "present",
  "preview",
  "select",
  "show"
];
var BackendActions = [
  "close",
  "create",
  "delete",
  "download",
  "duplicate",
  "list",
  "open",
  "read",
  "return",
  "score",
  "search",
  "start",
  "submit",
  "update",
  "upload"
];

// src/lib/plugins/DpAnalyticsValidationPlugin/errorMessages.ts
var ERROR_MESSAGES = {
  ELEMENT_NAME_TOO_LONG: "The element name string is too long. It should be no longer than 45 characters.",
  INVALID_ELEMENT_STRING: "The element name is not a valid string.",
  ELEMENT_NAME_CONTAINS_SPACES: "The element name should not contain spaces.",
  EVENT_NAME_TOO_LONG: "The event string is too long. It should be no longer than 45 characters.",
  INVALID_EVENT_STRING: "The event is not a valid string.",
  EVENT_NAME_CONTAINS_SPACES: "The event name should not contain spaces.",
  INVALID_EVENT_NAME: "The event name does not contain a valid object_action combination.",
  IDENTIFY_INVALID_PARAMS: "Identify all required params and make sure to have the correct datatypes.",
  INVALID_TIME_ON_PAGE: "Add a valid timeOnPage and idleTime",
  INVALID_TITLE_URL: "Add a valid title and url",
  MISSING_ELEMENT_NAME: "The event must contain an element name.",
  MISSING_EVENT_PREFIX: "The event name is missing the ux_ prefix"
};

// src/lib/plugins/DpAnalyticsValidationPlugin/validation.utils.ts
var validateUser = (userId, traits) => validateStringExists(userId) && validateStringExists(traits?.activeRole);
function validateStringExists(item) {
  return !!item && typeof item === "string";
}
function validateStringOptional(item) {
  if (!item) {
    return true;
  }
  return validateStringExists(item);
}
function validateInt(item) {
  return Number.isInteger(item) && !!item;
}
function validateIntOptional(item) {
  if (!item) {
    return true;
  }
  return validateInt(item);
}
function validateEventAction(item, isBE = false) {
  if (isBE && item === "page_view") {
    return true;
  }
  const actions = isBE ? BackendActions : FrontEndActions;
  const eventAction = item.slice(item.lastIndexOf("_") + 1);
  return actions.includes(eventAction);
}
function validateUxPrefix(eventName) {
  return eventName.startsWith("ux_");
}
function validateGALength(item) {
  return item.length <= 45;
}
function getValidationErrorMessage(type) {
  return ERROR_MESSAGES[type] || "Invalid input";
}
function validateEvent(item) {
  if (!validateStringExists(item)) {
    return getValidationErrorMessage("INVALID_EVENT_STRING");
  }
  if (item.includes?.(" ")) {
    return getValidationErrorMessage("EVENT_NAME_CONTAINS_SPACES");
  }
  if (!validateGALength(item)) {
    return getValidationErrorMessage("EVENT_NAME_TOO_LONG");
  }
  if (!validateEventAction(item)) {
    return getValidationErrorMessage("INVALID_EVENT_NAME");
  }
  if (!validateUxPrefix(item)) {
    return getValidationErrorMessage("MISSING_EVENT_PREFIX");
  }
  return "";
}
function validateElement(item) {
  if (!validateStringExists(item)) {
    return getValidationErrorMessage("INVALID_ELEMENT_STRING");
  }
  if (item.includes?.(" ")) {
    return getValidationErrorMessage("ELEMENT_NAME_CONTAINS_SPACES");
  }
  if (!validateGALength(item)) {
    return getValidationErrorMessage("ELEMENT_NAME_TOO_LONG");
  }
  return "";
}

// src/lib/utils/logger.utils.ts
var log = (typeLog, title, message, content) => {
  const emoji = {
    debug: String.fromCodePoint(128030),
    warning: String.fromCodePoint(9888),
    error: String.fromCodePoint(10060)
  };
  console.group(`${emoji[typeLog]} ${typeLog.toUpperCase()} - DpAnalytics - ${title}`);
  if (message) {
    console.log(message);
  }
  if (content) {
    console.log({
      timestamp: Date.now(),
      content
    });
  }
  console.groupEnd();
};
var error = (title, message, content) => {
  log("error", title, message, content);
};

// src/lib/plugins/DpAnalyticsValidationPlugin/DpAnalyticsValidationPlugin.ts
var DP_ANALYTICS_CUSTOM_PLUGIN_VALIDATOR_NAME = "dp-analytics-lib-validator";
var DpAnalyticsValidationPlugin = {
  name: DP_ANALYTICS_CUSTOM_PLUGIN_VALIDATOR_NAME,
  identifyStart: ({ payload, abort }) => {
    if (!validateUser(payload.userId, payload.traits)) {
      const errorMessage = getValidationErrorMessage("IDENTIFY_INVALID_PARAMS");
      error("Identify", errorMessage, payload);
      return abort("[Identify] " + errorMessage);
    }
  },
  trackStart: ({ payload, abort }) => {
    let errorMessage = "";
    const eventError = validateEvent(payload.event);
    const elementError = validateElement(payload.properties?.elementName);
    if (eventError != "") {
      errorMessage = eventError;
    }
    if (elementError != "") {
      errorMessage = elementError;
    }
    if (errorMessage) {
      error("Track", errorMessage, payload);
      return abort("[Track] " + errorMessage);
    }
  },
  pageStart: ({ payload, abort }) => {
    let errorMessage = "";
    if (!validateIntOptional(payload.properties?.timeOnPage) || !validateIntOptional(payload.properties?.idleTime)) {
      errorMessage = getValidationErrorMessage("INVALID_TIME_ON_PAGE");
    }
    if (!validateStringOptional(payload.properties?.url) || !validateStringOptional(payload.properties?.title)) {
      errorMessage = getValidationErrorMessage("INVALID_TITLE_URL");
    }
    if (errorMessage) {
      error("Page", errorMessage, payload);
      return abort("[Page] " + errorMessage);
    }
  }
};

// src/lib/utils/helpers.utils.ts
var isServer = () => typeof window === "undefined";
function flattenEventPayload(evtPayload, prefix = "", res = {}) {
  for (const [key, value] of Object.entries(evtPayload)) {
    const newKey = prefix ? `${prefix}_${key}` : key;
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      flattenEventPayload(value, newKey, res);
    } else {
      res[newKey] = value;
    }
  }
  return res;
}

// src/lib/DpAnalytics.ts
var DpAnalytics = (config) => {
  if (isServer())
    return;
  try {
    if (window.dpAnalyticsInstance) {
      return window.dpAnalyticsInstance;
    }
    if (!config) {
      throw new Error("Please instantiate the library");
    }
    const plugins = [DpAnalyticsValidationPlugin];
    plugins.push(
      googleAnalytics({
        measurementIds: [config.googleAnalyticsId],
        debug: config.debug ?? false
      })
    );
    const analytics = Analytics({
      app: config.appName,
      debug: config.debug ?? false,
      plugins
    });
    window.dpAnalyticsInstance = {
      ...analytics,
      identify(userId, traits) {
        if (!userId) {
          return error("Identify User", "You must provide a valid userId");
        }
        return analytics.identify(MD5(userId).toString(), traits);
      },
      track(eventName, payload) {
        const trackPayload = {
          event: "ClickEvent",
          action: "Clicked",
          appName: config?.appName,
          ...payload
        };
        return analytics.track(eventName, trackPayload);
      },
      page(data) {
        const pagePayload = {
          event: "ViewEvent",
          action: "Viewed",
          appName: config?.appName,
          ...data
        };
        return analytics.page(pagePayload);
      }
    };
    return window.dpAnalyticsInstance;
  } catch (err) {
    error("DpAnalytics", err.message || "Could not instantiate library", err);
    return {};
  }
};

// src/lib/plugins/DpAnalyticsServerPlugin/DpAnalyticsServerPlugin.ts
import axios from "axios";
var DpAnalyticsServerMP = class {
  API_SECRET;
  MEASUREMENT_ID;
  GA_ENDPOINT;
  DEBUG_ENDPOINT;
  CLIENT_ID;
  constantParams;
  hitBuffer;
  /**
   * Constructor for DpAnalyticsServerMP class
   * @param {string} apiSecret - Google Analytics API secret
   * @param {string} measurementId - Google Analytics Measurement ID
   * @param {string} clientId - Client ID associated with the analytics data
   */
  constructor(apiSecret, measurementId, clientId) {
    this.API_SECRET = apiSecret;
    this.MEASUREMENT_ID = measurementId;
    this.CLIENT_ID = clientId;
    this.GA_ENDPOINT = `https://www.google-analytics.com/mp/collect?measurement_id=${this.MEASUREMENT_ID}&api_secret=${this.API_SECRET}`;
    this.DEBUG_ENDPOINT = `https://www.google-analytics.com/debug/mp/collect?measurement_id=${this.MEASUREMENT_ID}&api_secret=${this.API_SECRET}`;
    this.constantParams = {};
    this.hitBuffer = [];
  }
  readClientInfo() {
    console.log(`
      *** Connection details for GA4 Connection ***
      API_SECRET: ${this.API_SECRET}
      MEASUREMENT_ID: ${this.MEASUREMENT_ID}
      CLIENT_ID: ${this.CLIENT_ID}
      GA_ENDPOINT: ${this.GA_ENDPOINT}
      DEBUG_ENDPOINT: ${this.DEBUG_ENDPOINT}
    `);
  }
  setConstantParam(key, value) {
    this.constantParams[key] = value;
  }
  unsetConstantParam(key) {
    delete this.constantParams[key];
  }
  addConstantParamsToEvents(events) {
    for (let i = 0; i < events.length; i++) {
      for (const param in this.constantParams) {
        events[i].params[param] = this.constantParams[param];
      }
    }
  }
  track(eventName, payload, postpone = false) {
    if (!validateStringExists(eventName)) {
      return Promise.reject(new Error(getValidationErrorMessage("INVALID_EVENT_STRING")));
    }
    if (eventName?.includes?.(" ")) {
      return Promise.reject(new Error(getValidationErrorMessage("EVENT_NAME_CONTAINS_SPACES")));
    }
    if (!validateGALength(eventName)) {
      return Promise.reject(new Error(getValidationErrorMessage("EVENT_NAME_TOO_LONG")));
    }
    if (!validateEventAction(eventName, true)) {
      return Promise.reject(new Error(getValidationErrorMessage("INVALID_EVENT_NAME")));
    }
    const trackEvent = {
      name: eventName,
      params: {
        ...this.constantParams,
        ...flattenEventPayload(payload ?? {})
      }
    };
    if (postpone) {
      this.hitBuffer.push(trackEvent);
      return Promise.resolve();
    } else {
      return this.send([trackEvent]);
    }
  }
  async send(events, postpone = false) {
    const eventMax = events.length > 25;
    this.addConstantParamsToEvents(events);
    this.nullParamCheck(events);
    if (postpone) {
      events.forEach((event) => this.hitBuffer.push(event));
      return Promise.resolve();
    } else {
      if (eventMax) {
        await this.sendLargeEventCount(events);
      } else {
        await axios.post(this.GA_ENDPOINT, {
          client_id: this.CLIENT_ID,
          events
        }).then(() => {
          return Promise.resolve();
        }).catch((error2) => {
          return Promise.reject(error2);
        });
      }
    }
  }
  async flushBuffer() {
    await this.sendLargeEventCount(this.hitBuffer);
  }
  async sendLargeEventCount(events) {
    while (events.length > 0) {
      const eventBlock = events.splice(0, 25);
      await this.send(eventBlock);
    }
    return Promise.resolve();
  }
  nullParamCheck(events) {
    events.forEach((event) => {
      for (const param in event.params) {
        if (event.params[param] === null) {
          console.log(`Removed null param for '${param}' in '${event.name}'`);
          delete event.params[param];
        }
      }
    });
  }
};
function createClient(apiSecret, measurementId, clientId) {
  return new DpAnalyticsServerMP(apiSecret, measurementId, clientId);
}
export {
  BackendActions,
  DpAnalytics,
  DpAnalyticsServerMP,
  FrontEndActions,
  createClient
};
